/**
 * RebookDialog.jsx — replaces the legacy window.confirm() rebook prompt.
 *
 * Shows the source reference, lets the user pick a new date+time, and
 * previews exactly which fields will carry over into the draft vs
 * which will be cleared (per the rebookBooking contract locked by
 * `lib/api/bookings/__tests__/rebook.test.js`).
 *
 * Shipped 2026-07-14 as a follow-up to the R-1..R-7 refactor track.
 */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AuDateInput, AuTimeInput } from "@/components/AuDateTime";
import { CalendarClock, AlertCircle } from "lucide-react";

/** Fields cleared by rebookBooking (source-of-truth mirror — keep in
 *  sync with `lib/api/bookings/crud.js` rebookBooking implementation
 *  and the invariants in `__tests__/rebook.test.js`). */
const CLEARED_ON_REBOOK = [
  { key: "reference_number",       reason: "regenerated by DB trigger" },
  { key: "checkin_time",           reason: "lifecycle timestamp" },
  { key: "checkout_time",          reason: "lifecycle timestamp" },
  { key: "arrival_time",           reason: "lifecycle timestamp" },
  { key: "checked_in_at / by",     reason: "lifecycle audit" },
  { key: "arrival_capture_method", reason: "lifecycle audit" },
  { key: "approved_at / by",       reason: "approval audit" },
  { key: "completed_at",           reason: "lifecycle timestamp" },
  { key: "cancelled_at",           reason: "lifecycle timestamp" },
  { key: "no_show_at",             reason: "lifecycle timestamp" },
  { key: "hold_set_at / by",       reason: "hold-workflow audit" },
  { key: "qa_confirmed_at / by",   reason: "QA audit" },
  { key: "recurring_pattern_id",   reason: "clone is not a pattern occurrence" },
  { key: "is_recurring",           reason: "clone is not a pattern occurrence" },
];

const CARRIED_ON_REBOOK = [
  "site + bay + direction",
  "sender / receiver / vehicle info",
  "goods + pallets + weight",
  "compliance flags + QA confirmations",
  "notes + external refs (PO, manifest, etc.)",
  "assigned driver (unless you reset below)",
];

const pad = (n) => String(n).padStart(2, "0");
const toLocalInputs = (iso) => {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

export default function RebookDialog({ open, source, onClose, onConfirm }) {
  // Defaults: +24 h from the original booking (matches the legacy behaviour).
  const defaultStart = useMemo(() => {
    if (!source?.scheduled_start) return null;
    return new Date(new Date(source.scheduled_start).getTime() + 24 * 60 * 60 * 1000);
  }, [source]);
  const defaultEnd = useMemo(() => {
    if (!source?.scheduled_end) return null;
    return new Date(new Date(source.scheduled_end).getTime() + 24 * 60 * 60 * 1000);
  }, [source]);

  const [{ date, startTime, endTime }, setForm] = useState({ date: "", startTime: "", endTime: "" });
  const [resetDriver, setResetDriver] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !source) return;
    const s = toLocalInputs(defaultStart?.toISOString());
    const e = toLocalInputs(defaultEnd?.toISOString());
    setForm({ date: s.date, startTime: s.time, endTime: e.time });
    setResetDriver(false);
  }, [open, source, defaultStart, defaultEnd]);

  if (!source) return null;

  const canSubmit = !!(date && startTime && endTime) && !submitting;

  const submit = async () => {
    setSubmitting(true);
    const newStart = new Date(`${date}T${startTime}:00`).toISOString();
    const newEnd   = new Date(`${date}T${endTime}:00`).toISOString();
    try {
      await onConfirm({ newStart, newEnd, resetDriver });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="rebook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-slate-500" />
            Rebook <span className="font-mono text-base">{source.reference_number}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* ── New schedule ── */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="rebook-date" className="text-xs">New date</Label>
              <AuDateInput
                id="rebook-date"
                data-testid="rebook-date" required
                value={date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="rebook-start" className="text-xs">Start time</Label>
              <AuTimeInput
                id="rebook-start"
                data-testid="rebook-start"
                value={startTime}
                onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="rebook-end" className="text-xs">End time</Label>
              <AuTimeInput
                id="rebook-end"
                data-testid="rebook-end"
                value={endTime}
                onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
              />
            </div>
          </div>

          {/* ── Optional: reset assigned driver ── */}
          <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              data-testid="rebook-reset-driver"
              checked={resetDriver}
              onChange={(e) => setResetDriver(e.target.checked)}
            />
            <div className="text-sm">
              <div className="font-medium">Reset assigned driver</div>
              <div className="text-xs text-slate-500">
                Clone will start with no driver assigned. Otherwise the same
                driver from <span className="font-mono">{source.reference_number}</span> is used.
              </div>
            </div>
          </label>

          {/* ── Two-column carry-over preview ── */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-100">
              <div className="font-semibold text-emerald-800 mb-2">Will carry over</div>
              <ul className="space-y-1 text-emerald-900">
                {CARRIED_ON_REBOOK.map((c) => (
                  <li key={c}>• {c}</li>
                ))}
              </ul>
            </div>
            <div className="p-3 rounded-md bg-amber-50 border border-amber-100">
              <div className="font-semibold text-amber-800 mb-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Will be cleared
              </div>
              <ul className="space-y-1 text-amber-900 font-mono text-[11px]">
                {CLEARED_ON_REBOOK.map((c) => (
                  <li key={c.key} title={c.reason}>• {c.key}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="rebook-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="rebook-confirm">
            {submitting ? "Rebooking…" : "Create rebook draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
